import * as React from 'react'

import { cn } from '../../lib/utils'

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div className="ds-relative ds-z-50 ds-w-full">
    <table
      ref={ref}
      className={cn(
        'table-component ds-text-sm ds-rounded-none ds-selection-bg-gray-100 ds-selection-text-gray-900 ds-relative ds-w-full ds-caption-bottom ds-border-x-[0px] ds-border-[#E5E7EB] ds-bg-[#FFFFFF]',
        className,
      )}
      style={{ position: 'relative' }}
      {...props}
    />
  </div>
))
Table.displayName = 'Table'

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      '[&_tr]:ds-border-b',
      'ds-font-weight-500 ds-font-text ds-uppercase ds-text-black-700',
      'ds-z-80 ds-sticky ds-top-0 ds-bg-white-0 ds-bg-clip-padding',
      'ds-transition-colors ds-duration-200',
      className,
    )}
    style={{
      position: 'sticky',
      top: 0,
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    }}
    {...props}
  />
))
TableHeader.displayName = 'TableHeader'

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn('[&_tr:last-child]:ds-border-0', className)}
    {...props}
  />
))
TableBody.displayName = 'TableBody'

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      'ds-border-t ds-bg-muted/50 ds-font-medium ds-[&>tr]:ds-last:ds-border-b-0',
      className,
    )}
    {...props}
  />
))
TableFooter.displayName = 'TableFooter'

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => {
  const status = (props as unknown as { 'data-status': string })['data-status']
  const hoverClass = status
    ? status.toLowerCase() === 'success'
      ? 'hover:ds-bg-green-50/20'
      : status.toLowerCase() === 'error' || status.toLowerCase() === 'failed'
        ? 'hover:ds-bg-red-50/20'
        : 'hover:ds-bg-muted/50'
    : 'hover:ds-bg-muted/50'

  return (
    <tr
      ref={ref}
      className={cn(
        'ds-data-[state=selected]:ds-bg-muted ds-cursor-pointer ds-border-b-half ds-border-white-200 ds-text-black-700 ds-transition-colors',
        hoverClass,
        className,
      )}
      style={{
        ...props.style,
      }}
      {...props}
    />
  )
})
TableRow.displayName = 'TableRow'

const TableHead = React.forwardRef<
  HTMLTableHeaderCellElement,
  React.ThHTMLAttributes<HTMLTableHeaderCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'ds-font-10 ds-align- middle ds-text-left ds-font-text ds-text-12 ds-font-500',
      'ds-bg-white ds-h-[32px] ds-px-16 ds-font-text ds-uppercase',
      'ds-sticky ds-top-0 ds-z-50',
      className,
    )}
    style={{
      backgroundColor: 'inherit',
    }}
    {...props}
  />
))
TableHead.displayName = 'TableHead'

const TableCell = React.forwardRef<
  HTMLTableDataCellElement,
  React.TdHTMLAttributes<HTMLTableDataCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      'ds-flex ds-items-center ds-px-16',
      'ds-h-[40px] ds-min-h-[40px]',
      'ds-text-12',
      'ds-overflow-hidden',
      className,
    )}
    style={{
      minHeight: '40px',
      width: props.style?.width,
      maxWidth: props.style?.width,
      ...props.style,
    }}
    {...props}
  >
    <div className="ds-w-full ds-flex-1 ds-overflow-hidden ds-truncate ds-text-ellipsis ds-whitespace-nowrap">
      {props.children}
    </div>
  </td>
))
TableCell.displayName = 'TableCell'

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('ds-text-sm ds-text-muted-foreground ds-mt-4', className)}
    {...props}
  />
))
TableCaption.displayName = 'TableCaption'

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
}
